import { FC } from 'react'
import { ManageAccount } from '../ManageAccount/ManageAccount'
import { VerifyEmail } from '../VerifyEmail/VerifyEmail'
import { ChangeName, ChangeNameProps } from '../ChangeName/ChangeName'
import { ChangeEmail } from '../ChangeEmail/ChangeEmail'
import { DeleteAccount } from '../DeleteAccount/DeleteAccount'
import {
    ChangePassword,
    ChangePasswordProps,
} from '../ChangePassword/ChangePassword'
import { DeleteAccountAcknowledge } from '../DeleteAccount/DeleteAccountAcknowledge'
import { DeleteAccountPassword } from '../DeleteAccount/DeleteAccountPassword'
import { ManageAccountNotImplemented } from '../NotImplemented/NotImplemented'
import { EmailPreference } from '../EmailPreference/EmailPreference'
import { Product } from '@news-mono/web-common'

export type ScreenType =
    | 'manage-account'
    | 'email-preference'
    | 'about-us'
    | 'verify-email'
    | 'change-name'
    | 'change-email'
    | 'change-password'
    | 'delete-account'
    | 'delete-account-acknowledge'
    | 'delete-account-password'

interface ScreenConfig {
    messageText?: string
    headingText: string
    subHeadingText?: string
    // If a parent is provided, it'll use this to navigate and get the back text.
    parent?: ScreenType
    // We want to utilise nav text variable for the top level for mobile devices that have
    // it being displayed in two layers.
    navText?: string
    body: FC<ChangeNameProps | ChangePasswordProps>
}

/**
 * Used as an exposed access point to get all screens, dependant on each specific masthead!
 * It is recommended to store these as a variable, to save the infinite recall of them.
 *
 * @param product - the masthead the request is being made on, to determine the screens to use
 * @returns a record of ScreenType to ScreenConfig
 */
export const getScreens = (
    product: Product,
): Record<ScreenType, ScreenConfig> => {
    if (product === Product.PerthNow) {
        return perthNowScreens
    }

    return theNightlyScreens
}

const theNightlyScreens: Record<ScreenType, ScreenConfig> = {
    'manage-account': {
        navText: 'Settings',
        messageText: undefined,
        headingText: 'Manage account',
        body: ManageAccount,
    },
    'verify-email': {
        navText: 'Settings',
        headingText: 'Verify your email',
        parent: 'manage-account',
        body: VerifyEmail,
    },
    'change-password': getManageAccountChild('Change password', ChangePassword),
    'change-name': getManageAccountChild('Change name', ChangeName),
    'change-email': getManageAccountChild(
        'Change email',
        ChangeEmail,
        'Edit or change your email.',
    ),
    'delete-account': getManageAccountChild(
        'Delete your account',
        DeleteAccount,
    ),
    'delete-account-acknowledge': {
        navText: 'Delete account',
        headingText: 'Permanently delete your account?',
        parent: 'delete-account',
        body: DeleteAccountAcknowledge,
    },
    'delete-account-password': {
        navText: 'Delete account',
        headingText: 'Enter your password to delete your account',
        parent: 'delete-account-acknowledge',
        body: DeleteAccountPassword,
    },
    'email-preference': {
        navText: 'Manage email preferences',
        headingText: 'Email preferences',
        parent: 'email-preference',
        body: EmailPreference
    },
    'about-us': getNotImplemented(),
}

const perthNowScreens: Record<ScreenType, ScreenConfig> = {
    ...theNightlyScreens,
    'change-email': getManageAccountChild('Change email', ChangeEmail),
    'email-preference': getNotImplemented(),
    'about-us': getNotImplemented(),
    'delete-account': getManageAccountChild('Delete Account', DeleteAccount),
}

function getManageAccountChild(
    headingText: string,
    body: FC<ChangeNameProps>,
    subHeadingText?: string,
    messageText?: string,
): ScreenConfig {
    return {
        headingText,
        subHeadingText,
        parent: 'manage-account',
        body,
        messageText,
    }
}

function getNotImplemented(): ScreenConfig {
    return {
        navText: 'Settings',
        headingText: 'Not Implemented.',
        parent: 'manage-account',
        body: ManageAccountNotImplemented,
    }
}
